import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Mail, MessageCircle, MapPin, Phone } from "lucide-react";
import { useState } from "react";

export function ContactSection() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    const payload = {
      name: formData.get("name"),
      email: formData.get("email") || "Not provided",
      mobile: formData.get("mobile"),
      message: formData.get("message") || "No message provided",
    };

    try {
      const response = await fetch("/api/send-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSuccess(true);
        form.reset();
        setTimeout(() => {
          setIsSuccess(false);
        }, 5000);
      } else {
        setErrorMessage(data.error || "Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setErrorMessage("Network error. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 md:py-24 bg-muted/50">
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Contact Us</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Have questions about our products? Get in touch with our team.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 md:gap-12">
          <div className="bg-background p-6 rounded-lg border">
            <form className="space-y-4" onSubmit={handleSubmit}>
              {isSuccess && (
                <div className="p-4 mb-4 text-sm text-green-700 bg-green-100 rounded-lg">
                  Thank you for your message! We'll get back to you soon.
                </div>
              )}
              {errorMessage && (
                <div className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg">
                  {errorMessage}
                </div>
              )}
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <Input id="name" name="name" placeholder="Your name" required />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-1">
                  Email
                </label>
                <Input id="email" name="email" type="email" placeholder="your@email.com" />
              </div>
              <div>
                <label htmlFor="mobile" className="block text-sm font-medium mb-1">
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <Input
                  id="mobile"
                  name="mobile"
                  type="tel"
                  placeholder="+91 XXXXX XXXXX"
                  pattern="[+]?[0-9\s\-]{7,15}"
                  title="Enter a valid phone number"
                  required
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium mb-1">
                  Message
                </label>
                <Textarea id="message" name="message" placeholder="How can we help you?" rows={4} />
              </div>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </div>

          <div className="space-y-6">
            <div className="bg-background p-6 rounded-lg border">
              <div className="flex items-start space-x-4">
                <MapPin className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <h3 className="font-semibold text-lg mb-2">Our Location</h3>
                  <a href="https://maps.app.goo.gl/aP9CUGeJo7cCCuvE8" className="text-muted-foreground">
                    3, JADESHWAR CHAMBER, 8-A National Highway, <br />
                    Wankaner, Dist MORBI-363622.
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-background p-6 rounded-lg border">
              <div className="flex items-start space-x-4">
                <Mail className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <h3 className="font-semibold text-lg mb-2">Email Us</h3>
                  <a href="mailto:avonbearingswkr@gmail.com" className="text-muted-foreground">
                    avonbearingswkr@gmail.com
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-background p-6 rounded-lg border">
              <div className="flex items-start space-x-4">
                <Phone className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <h3 className="font-semibold text-lg mb-2">Call Us</h3>
                  <a href="tel:+919664585193" className="text-muted-foreground">
                    +91 96645 85193
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-background p-6 rounded-lg border">
              <div className="flex items-start space-x-4">
                <MessageCircle className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <h3 className="font-semibold text-lg mb-2">WhatsApp</h3>
                  <a href="https://wa.me/919664585193" className="text-muted-foreground">
                    +91 96645 85193<br />
                    Available Mon-Sat, 9am to 7pm
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

